import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Markdown from "markdown-to-jsx";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import get from "lodash/get";

import { theme } from "../../../utils/global-styles/theme";
import AscendingCascadingCards from "../../AscendingCascadingCards/AscendingCascadingCards";

const OurStoryHero = ({
  heading,
  heroImage,
  acumenBenefitsObject,
  ourStoryTitleAndDescription,
}) => {
  const HeroGrid = styled.div`
      display: grid;
      grid-template-rows: 611px 100px;
      grid-template-columns: 1fr;
      font-family: ${theme.fontFaces.headers};

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        grid-template-rows: 270px 100px;
      }
    `,
    HeroImg = styled(GatsbyImage)`
      grid-area: 1 / 1 / span 2 / span 3;
    `,
    HeroImgPlaceholder = styled.div`
      grid-area: 1 / 1 / span 2 / span 3;
    `,
    ImageOverlayTextContainer = styled.div`
      grid-area: 1 / 1 / span 2 / span 3;
      padding: 109px 80px;
      z-index: 1;
      background: linear-gradient(
        350.52deg,
        rgba(50, 50, 50, 0) 0%,
        rgba(50, 50, 50, 0.3) 100%
      );

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        padding: 109px 50px;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        padding: 50px 41px;
      }
    `,
    BenefitCardsWrapper = styled.div`
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: center;
      margin-top: 50px;

      @media only screen and (min-width: ${theme.breakpoints
        .iPadPro}) {
          flex-direction: row;
        }
    `,
    AAHeading = styled.h3`
      font-size: 23px;
      font-weight: bold;
      letter-spacing: -0.96px;
      line-height: 28px;
      margin-bottom: 6px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 12px;
        line-height: 14px;
        margin-bottom: 0px;
      }
    `,
    AASlogan = styled.h3`
      font-size: 21px;
      font-weight: 300;
      letter-spacing: -0.88px;
      line-height: 25px;
      margin-bottom: 44px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        margin-bottom: 14px;
      }
    `,
    HeroHeading = styled.h1`
      font-size: 130px;
      font-weight: bold;
      letter-spacing: -6.25px;
      line-height: 130px;
      max-width: 840px;

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        font-size: 10vw;
        letter-spacing: -3.25px;
        line-height: 10vw;
        max-width: 640px;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 40px;
        letter-spacing: -1.92px;
        line-height: 40px;
        max-width: 250px;
      }
    `,
    BenefitCard = styled.div`
      height: 376px;
      width: 100%;
      padding: 45px;
      box-shadow: 10px 10px 20px 0 rgba(0, 0, 0, 0.1);
      z-index: 2;

      @media only screen and (max-width: ${theme.breakpoints.helper1350}) {
        padding: 25px;
      }

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        height: auto;
        padding: 14px 14px 50px 14px;
      }
    `,
    BenefitHeading = styled.h2`
      font-size: 40px;
      font-weight: bold;
      letter-spacing: -1.04px;
      line-height: 48px;
      margin-bottom: 12px;

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        font-size: 32px;
        letter-spacing: -0.83px;
        line-height: 38px;
      }
    `,
    BenefitDescription = styled.h3`
      font-size: 30px;
      font-weight: bold;
      letter-spacing: -0.09px;
      line-height: 37px;

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        font-size: 18px;
        font-weight: 500;
        letter-spacing: -0.39px;
        line-height: 26px;
      }
    `,
    StoryTitleAndDescriptionContainer = styled.div`
      grid-area: 4 / 1 / span 1 / span 3;
      padding: 60px 342px;
      margin-top: 50px;

      @media only screen and (min-width: ${theme.breakpoints
        .tabletUp}) and (max-width: ${theme.breakpoints.iPadPro}) {
          padding: 60px 50px;
      }
      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        padding: 60px 40px 60px 40px;
      }
    `,
    StoryTitle = styled.h2`
      font-size: 53px;
      font-weight: bold;
      letter-spacing: -1.38px;
      line-height: 53px;
      margin-bottom: 50px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 40px;
        letter-spacing: -1.92px;
        line-height: 40px;
        margin-bottom: 20px;
      }
    `;

  const generateBenefitCard = (benefitsObject, cardNumber) => {
    let marginTop = 150 - 50 * cardNumber;
    let cardClassNames = cardNumber % 2 === 0 ? "light-bg" : "white primary-bg";
    return (
      benefitsObject[`benefit${cardNumber}`] && (
        <BenefitCard
          className={cardClassNames}
          css={css`
            grid-area: 2 / ${cardNumber} / span 2 / span 1;
            margin-top: ${marginTop}px;

            @media only screen and (max-width: ${theme.breakpoints.helper800}) {
              grid-area: ${cardNumber + 2} / 1 / span 1 / span 3;
              margin-top: 0;
            }
          `}
        >
          <BenefitHeading>
            {benefitsObject[`benefit${cardNumber}`]}
          </BenefitHeading>
          <BenefitDescription>
            {benefitsObject[`description${cardNumber}`]}
          </BenefitDescription>
        </BenefitCard>
      )
    );
  };

  const transformObject = obj => {
    const transformedArray = [];

    for (let i = 1; i <= 3; i++) {
      const benefitKey = `benefit${i}`;
      const descriptionKey = `description${i}`;

      if (obj[benefitKey] && obj[descriptionKey]) {
        transformedArray.push({
          benefit: obj[benefitKey],
          description: obj[descriptionKey],
        });
      }
    }

    return transformedArray;
  };
  const transformedArray = transformObject(acumenBenefitsObject);
  return (
    <HeroGrid>
      {heroImage && heroImage.image && heroImage.image.gatsbyImageData ? (
        <HeroImg
          image={getImage(heroImage?.image?.gatsbyImageData)}
          alt={heroImage.imageDescription}
        />
      ) : (
        <HeroImgPlaceholder className="secondary-bg" />
      )}
      <ImageOverlayTextContainer className="white">
        <HeroHeading>{heading}</HeroHeading>
      </ImageOverlayTextContainer>
      <BenefitCardsWrapper>
        <AscendingCascadingCards cards={transformedArray} />
      </BenefitCardsWrapper>

      <StoryTitleAndDescriptionContainer>
        <StoryTitle>
          {get(
            ourStoryTitleAndDescription,
            "title",
            "Doing what’s right in a world that loves easy"
          )}
        </StoryTitle>
        <Markdown
          options={{
            overrides: {
              p: {
                props: {
                  className: "our-story-description",
                },
              },
            },
          }}
        >
          {get(ourStoryTitleAndDescription, "description.description", "")}
        </Markdown>
      </StoryTitleAndDescriptionContainer>
    </HeroGrid>
  );
};

OurStoryHero.propTypes = {
  heading: PropTypes.string,
  heroImage: PropTypes.shape({
    image: PropTypes.shape({
      fluid: PropTypes.shape({
        srcSet: PropTypes.string,
        aspectRatio: PropTypes.number,
      }),
    }),
    imageDescription: PropTypes.string,
  }),
  acumenBenefitsObject: PropTypes.shape({
    benefit1: PropTypes.string,
    description1: PropTypes.string,
    benefit2: PropTypes.string,
    description2: PropTypes.string,
    benefit3: PropTypes.string,
    description3: PropTypes.string,
  }),
  ourStoryTitleAndDescription: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.shape({
      description: PropTypes.string,
    }),
  }),
};

export default OurStoryHero;

